/**
 * Single row in the EffectsEditor: `[variable] [op] [value]` plus a remove
 * button.
 *
 * Type-driven UI like ComparisonRow. The `add` op is only offered for number
 * variables (the prototype's discipline; subtraction is done via negative
 * literals).
 *
 * When the user changes which variable the row references:
 *   - if the new variable's type doesn't support the current op (e.g.,
 *     switching from a number with `add` to a boolean), the op resets to `set`
 *   - the value resets to the new variable's default (coercion is rarely
 *     what authors mean)
 */
import { X } from 'lucide-react';
import type { Effect, EffectOp, VariableDecl } from '@branchworks/shared';

interface EffectRowProps {
  effect: Effect;
  variables: Record<string, VariableDecl>;
  onChange: (patch: Partial<Effect>) => void;
  onDelete: () => void;
}

export function EffectRow({ effect, variables, onChange, onDelete }: EffectRowProps) {
  const decl = variables[effect.variable];
  const orphan = !decl;

  const handleVariableChange = (newVarName: string) => {
    const newDecl = variables[newVarName];
    if (!newDecl) return;
    const newOp: EffectOp = effect.op === 'add' && newDecl.type !== 'number' ? 'set' : effect.op;
    onChange({ variable: newVarName, op: newOp, value: newDecl.default });
  };

  return (
    <div
      className="flex items-center gap-1 p-1 rounded border bg-cream-2"
      style={{
        borderColor: orphan ? '#A14A38' : '#E5DFD0',
      }}
    >
      <select
        value={effect.variable}
        onChange={(e) => handleVariableChange(e.target.value)}
        className="px-1.5 py-1 text-xs font-mono bg-white rounded border border-cream-border"
        style={{ maxWidth: 140 }}
        title={orphan ? 'This variable no longer exists' : (decl.description || effect.variable)}
      >
        {orphan && <option value={effect.variable}>{effect.variable} (deleted)</option>}
        {Object.keys(variables).map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
      <select
        value={effect.op}
        onChange={(e) => onChange({ op: e.target.value as EffectOp })}
        disabled={orphan}
        className="px-1 py-1 text-xs bg-white rounded border border-cream-border"
      >
        <option value="set">set</option>
        {decl?.type === 'number' && <option value="add">add</option>}
      </select>
      <div className="flex-1 min-w-0">{renderValueInput(effect, decl, onChange)}</div>
      <button
        onClick={onDelete}
        className="p-1 rounded hover:bg-cream flex-shrink-0 text-terracotta-dark"
        title="Remove effect"
        aria-label="Remove effect"
      >
        <X size={12} />
      </button>
    </div>
  );
}

function renderValueInput(
  effect: Effect,
  decl: VariableDecl | undefined,
  onChange: (patch: Partial<Effect>) => void,
) {
  if (!decl) {
    return (
      <input
        disabled
        value=""
        placeholder="(deleted variable)"
        className="w-full px-2 py-1 text-xs rounded border border-cream-border bg-white text-terracotta-dark"
      />
    );
  }
  if (decl.type === 'number') {
    return (
      <input
        type="number"
        value={Number(effect.value ?? 0)}
        onChange={(e) => {
          const n = Number(e.target.value);
          onChange({ value: Number.isFinite(n) ? n : 0 });
        }}
        className="w-full px-2 py-1 text-xs rounded border border-cream-border bg-white font-mono"
      />
    );
  }
  if (decl.type === 'boolean') {
    return (
      <select
        value={String(effect.value)}
        onChange={(e) => onChange({ value: e.target.value === 'true' })}
        className="w-full px-2 py-1 text-xs rounded border border-cream-border bg-white"
      >
        <option value="false">false</option>
        <option value="true">true</option>
      </select>
    );
  }
  return (
    <input
      type="text"
      value={String(effect.value ?? '')}
      onChange={(e) => onChange({ value: e.target.value })}
      className="w-full px-2 py-1 text-xs rounded border border-cream-border bg-white font-mono"
    />
  );
}
